// START-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen]
import { useEffect, useId, useRef } from 'react';
import { ReactComponent as BlockIcon } from '../../../assets/icons/block.svg';

interface FullPageMessageCardProps {
  title: string;
  message: string;
  actionLabel: string;
  onAction: () => void;
}

// Shared full-page takeover card for error/offline states. Both screens replace the whole
// route content, but the app Header stays rendered and reachable behind this card (no
// focus trap, no aria-modal) — so it is exposed as role="alert", not "alertdialog":
// alertdialog implies modal keyboard behavior (focus contained until dismissed), which this
// component doesn't provide, and claiming it would mislead assistive tech. Focus is moved
// to the heading on mount as the primary announcement mechanism; role="alert" additionally
// announces the container without claiming a live-region-driven read of the whole subtree.
export function FullPageMessageCard({ title, message, actionLabel, onAction }: FullPageMessageCardProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const baseId = useId();
  const titleId = `${baseId}-title`;
  const messageId = `${baseId}-message`;

  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  return (
    <div className="container d-flex align-items-center justify-content-center min-vh-100 overflow-y-auto py-3">
      <div
        className="card text-center shadow-lg full-page-message-card"
        role="alert"
        aria-labelledby={titleId}
        aria-describedby={messageId}
      >
        <div className="card-body p-3 p-md-5">
          <div className="mb-2 mb-md-4 text-warning">
            <BlockIcon className="bc-icon" aria-hidden="true" />
          </div>
          <h1 id={titleId} ref={headingRef} tabIndex={-1} className="mb-2 mb-md-3 fw-bold card-title h2">{title}</h1>
          <p id={messageId} className="mb-3 mb-md-4 card-text opacity-75">{message}</p>
          <div className="d-flex justify-content-center">
            <button className="btn btn-primary px-4 py-2 rounded-pill" onClick={onAction}>
              {actionLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
// /END-CodeLeap [17-Aug-2026]: [1.0-R-UX offline navigation screen]
